

import { useState, useEffect } from 'react'

import { Input } from './ui/shadcn/input'
import { FiDownload, FiX, FiPause, FiPlay, FiChevronRight } from 'react-icons/fi'
import { RiArrowGoBackFill } from "react-icons/ri";
import { useEpisode } from '@/contexts/EpisodeContext'
import Loader from './loader'
import useAudioController from '@/hooks/useAudioController'
import Button from '@/components/ui/Button'
import { Checkbox } from './ui/shadcn/checkbox'
import useFileDownload from '@/hooks/useFileDownload'
import dayjs from 'dayjs'

export default function ScriptHistory({onSelect, script, showHeader = true}) {
    const [search, setSearch] = useState('')
    const [history, setHistory] = useState([])
    const {episode, fetchScriptHistory} = useEpisode()
    const [loading, setLoading] = useState(true)
    const {playAudio, stopAudio, isAudioPlaying, currentAudio} = useAudioController()
    const [useParentScript, setUseParentScript] = useState(script ? true : false)
    const {downloadFile} = useFileDownload()

    const getData = async () => {
        try{
            setLoading(true)
            const h = await fetchScriptHistory(useParentScript ? script?._id : null)
            setHistory(h)
        }catch(error){
            console.log(error)
        }finally{
            setLoading(false)
        }
    }

    const handleAudioPreview = (url) => {
        if(isAudioPlaying && currentAudio === url){
            stopAudio()
        } else {
            playAudio(url)
        }
    }


    const handleDownload = async (element) => {
        await downloadFile(element.audio_url, `${element.character.name} - ${dayjs(element.createdAt).format('DD MMM | HH:mm A')}`)
    }

    const handleSelectHistory = (history) => {
        if(onSelect) onSelect(history)
    }

    useEffect(() => {
        // const filtered = search ? assets.filter((asset) => asset.name.toLowerCase().includes(search.toLowerCase())) : assets
        // setFilteredAssets(filtered)
        // setSelectedAsset(filtered.length ? filtered[0] : null)
    }, [search])

    useEffect(() => {
        getData()
    }, [useParentScript])

    return (
        <div className='w-full h-full flex flex-col gap-4 relative overflow-hidden'>
           {showHeader && <div >
                <h1 className='text-xl font-bold border-l-4 border-primary pl-4 capitalize'>History</h1>
            </div>}
            <div className='flex w-full relative px-4'>
                <Input type='text' placeholder='Search' className='w-full' value={search} onChange={(e) => setSearch(e.target.value)} />
                {search && <FiX className='w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-secondary-foreground/50 hover:text-secondary-foreground cursor-pointer' onClick={() => setSearch('')} />}
            </div>

            <div className='flex w-full gap-6 px-4'>
                <div className='flex items-center'>
                    <Checkbox id={`use-parent-script`} className='cursor-pointer' name={`use-parent-script`} checked={useParentScript} onCheckedChange={(checked) => setUseParentScript(checked)} />
                    <label htmlFor={`use-parent-script`} className='text-xs pl-2 text-secondary-foreground capitalize select-none cursor-pointer'>Element History</label>
                </div>
                <div className='flex items-center'>
                    <Checkbox id={`all-history`} className='cursor-pointer' name={`all-history`} checked={!useParentScript} onCheckedChange={(checked) => setUseParentScript(!checked)} />
                    <label htmlFor={`all-history`} className='text-xs pl-2 text-secondary-foreground capitalize select-none cursor-pointer'>All History</label>
                </div>
            </div>

            <div className='flex-1 overflow-y-auto px-4'>
                <div className='flex flex-col gap-8'>
                    {history.map((element) => (     
                        <div key={element._id} className='flex flex-col flex-1 gap-2 group'>
                            <div className='flex justify-between items-end mb-2'>
                                <div className='flex items-end gap-4'>
                                    <div className='rounded-full flex-none w-10 h-10 overflow-hidden bg-secondary'>
                                        {element.character.thumbnail_url && <img src={element.character.thumbnail_url} alt={element.character.name} loading="lazy" decoding="async" width={40} height={40} className='w-10 h-10 object-cover' />}
                                    </div>
                                    <div className='flex flex-col gap-1'>
                                        <span className='text-sm text-secondary-foreground'>{element.character.name}</span>
                                        <span className='text-[10px] text-secondary-foreground/80'>{dayjs(element.createdAt).format('DD MMM | HH:mm A')}</span>
                                    </div>
                                </div>
                                
                                <div className='flex gap-1 items-center opacity-0 group-hover:opacity-100'>
                                    <Button type='secondary' title='Play Audio' size='sm' icon={isAudioPlaying && currentAudio === element.audio_url ? <FiPause className='w-3 h-3' /> : <FiPlay className='w-3 h-3' />} onClick={() => handleAudioPreview(element.audio_url)} />
                                    <Button type='secondary' title='Download Audio' size='sm' icon={<FiDownload className='w-3 h-3' />} onClick={() => handleDownload(element)} />
                                    <Button type='secondary' title='Restore' size='sm' icon={<RiArrowGoBackFill className='w-3 h-3' />} onClick={() => handleSelectHistory(element)} />
                                </div>
                            </div>
                            <span className='text-xs/5 text-secondary-foreground font-mono italic '>{element.speech}</span>
                            <div className='flex items-center gap-1'>
                                {element.audio_duration !== null && <span className='text-[10px] text-secondary-foreground/80 bg-secondary/60 rounded-lg px-2 py-1'> Duration: {(element.audio_duration / 1000).toFixed(1)}s</span>}
                                {!Number.isNaN(element.voice_settings?.speed || NaN) && <span className='text-[10px] text-secondary-foreground/80 bg-secondary/60 rounded-lg px-2 py-1'> Speed: {element.voice_settings.speed}x</span>}
                                {!Number.isNaN(element.voice_settings?.stability || NaN) && <span className='text-[10px] text-secondary-foreground/80 bg-secondary/60 rounded-lg px-2 py-1'> Stability: {element.voice_settings.stability*100}%</span>}
                                {!Number.isNaN(element.voice_settings?.similarityBoost || NaN) && <span className='text-[10px] text-secondary-foreground/80 bg-secondary/60 rounded-lg px-2 py-1'> Similarity: {element.voice_settings.similarityBoost*100}%</span>}
                                {!Number.isNaN(element.voice_settings?.style || NaN) && <span className='text-[10px] text-secondary-foreground/80 bg-secondary/60 rounded-lg px-2 py-1'> Style: {element.voice_settings.style*100}%</span>}
                            </div>
                        </div>
                    ))}
                </div>
                {history.length === 0 && <div className='flex flex-1 items-center justify-center h-full'>
                    <span className='text-secondary-foreground/80 text-sm'>No assets found</span>
                </div>}
            </div>
            {loading && <Loader />}
        </div>
    )
} 